import React, { useEffect, useMemo, useState } from 'react';
import { TableSchema, TableColumn } from '../pages/App';
import { apiFetch } from '../api';

interface Props {
  schema: TableSchema;
  apiTableName: string;
  ids: Array<string | number>;
  filters: Record<string, any[]>;
  search: Record<string, string>;
  dateFrom?: string;
  dateTo?: string;
  month?: string;
  onClose: () => void;
  onSaved: (updatedCount: number) => void;
}

type FormState = Record<string, any>;

const BulkEditModal: React.FC<Props> = ({
  schema,
  apiTableName,
  ids,
  filters,
  search,
  dateFrom,
  dateTo,
  month,
  onClose,
  onSaved,
}) => {
  const [form, setForm] = useState<FormState>({});
  const [activeFields, setActiveFields] = useState<Record<string, boolean>>({});
  const [references, setReferences] = useState<Record<string, any[]>>({});
  const [isSaving, setIsSaving] = useState(false);

  const editableColumns = useMemo(() => {
    const columns = schema.columns.filter(c => c.key !== 'id');
    if (schema.name !== 'users') return columns;
    return columns.map(col =>
      col.key === 'password_hash'
        ? { ...col, key: 'password', label: col.label }
        : col
    );
  }, [schema]);

  const formatPromoDataValue = (value: any) => {
    if (value === undefined || value === null) return '';
    if (typeof value === 'string') return value;
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  };

  useEffect(() => {
    setForm({});
    const initialActive: Record<string, boolean> = {};
    editableColumns.forEach(col => {
      initialActive[col.key] = false;
    });
    setActiveFields(initialActive);
  }, [editableColumns]);

  useEffect(() => {
    editableColumns.forEach(async col => {
      if (col.type === 'reference' && col.refTable) {
        const res = await apiFetch(`/api/${col.refTable}`);
        let data = await res.json();
        if (
          col.refTable === 'articles' &&
          (schema.name === 'plan_records' || schema.name === 'finance_operations') &&
          col.key === 'article_id'
        ) {
          const map = new Map<number | null, any[]>();
          data.forEach((a: any) => {
            const pid = a.parentId ?? a.parent_id ?? null;
            if (!map.has(pid)) map.set(pid, []);
            map.get(pid)!.push(a);
          });
          const sortFn = (a: any, b: any) => {
            const gA = a.groupNumber ?? a.group_number ?? '';
            const gB = b.groupNumber ?? b.group_number ?? '';
            if (gA === gB) {
              const nA = a.number ?? '';
              const nB = b.number ?? '';
              return nA < nB ? -1 : nA > nB ? 1 : 0;
            }
            return gA < gB ? -1 : 1;
          };
          const build = (pid: number | null, level: number): any[] => {
            const items = map.get(pid) || [];
            items.sort(sortFn);
            const res: any[] = [];
            for (const it of items) {
              res.push({ ...it, _level: level });
              res.push(...build(it.id, level + 1));
            }
            return res;
          };
          data = build(null, 0);
        }
        setReferences(prev => ({ ...prev, [col.key]: data }));
      }
    });
  }, [editableColumns, schema]);

  const handleToggleField = (key: string) => {
    setActiveFields(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async () => {
    const activeKeys = Object.keys(activeFields).filter(key => activeFields[key]);
    if (activeKeys.length === 0) {
      alert('Выберите хотя бы одно поле для изменения.');
      return;
    }
    const values: Record<string, any> = {};
    activeKeys.forEach(key => {
      values[key] = form[key];
    });

    if (schema.name === 'plan_records' && values['date_month_end']) {
      const [y, m] = String(values['date_month_end']).split('-').map(Number);
      values['date_month_end'] = `${y}-${String(m).padStart(2, '0')}-01`;
    }
    if (schema.name === 'promo_task' && typeof values.data === 'string') {
      const trimmed = values.data.trim();
      if (!trimmed) {
        values.data = null;
      } else {
        try {
          values.data = JSON.parse(trimmed);
        } catch {
          values.data = trimmed;
        }
      }
    }
    Object.entries(values).forEach(([key, value]) => {
      if (value === '') {
        values[key] = null;
      }
      if (typeof value === 'string' && (value === 'true' || value === 'false')) {
        values[key] = value === 'true';
      }
    });

    const message = `Данные во всех записях (${ids.length}) будут изменены на указанные значения. ` +
      'Будут затронуты записи с учетом текущих фильтров. Продолжить?';
    if (!confirm(message)) return;

    setIsSaving(true);
    try {
      const res = await apiFetch(`/api/bulk-update/${apiTableName}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ids,
          values,
          filters,
          search,
          dateFrom,
          dateTo,
          month,
        }),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || res.statusText);
      }
      const json = await res.json();
      onSaved(json.updated ?? ids.length);
      onClose();
    } catch (error: any) {
      alert(error.message || 'Ошибка обновления');
    } finally {
      setIsSaving(false);
    }
  };

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    const confirmed = confirm(
      'Вы уверены, что хотите закрыть окно? Данные не сохранятся.'
    );
    if (confirmed) {
      onClose();
    }
  };

  const renderInput = (col: TableColumn) => {
    const isActive = !!activeFields[col.key];
    if (col.type === 'reference' && references[col.key]) {
      return (
        <select
          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 disabled:bg-slate-100"
          value={form[col.key] ?? ''}
          onChange={e => setForm({ ...form, [col.key]: e.target.value })}
          disabled={!isActive}
        >
          <option value="">--</option>
          {references[col.key].map(r => {
            const label =
              schema.name === 'finance_operations' &&
              (col.key === 'relation_id' || col.key === 'relationId')
                ? String(r.id)
                : (schema.name === 'plan_records' || schema.name === 'finance_operations') &&
                  col.key === 'article_id'
                ? `${r.code ?? ''} ${r.name ?? ''}`.trim()
                : r.alias && r.alias.trim()
                ? r.alias
                : r.name ?? (() => {
                    const fallbackKey = Object.keys(r).find(k => k !== 'id');
                    return fallbackKey ? r[fallbackKey] : '';
                  })();
            const pad = ' '.repeat((r._level || 0) * 2);
            return (
              <option key={r.id} value={r.id}>
                {pad + label}
              </option>
            );
          })}
        </select>
      );
    }
    if (col.type === 'boolean') {
      return (
        <select
          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 disabled:bg-slate-100"
          value={form[col.key] ?? ''}
          onChange={e => setForm({ ...form, [col.key]: e.target.value })}
          disabled={!isActive}
        >
          <option value="">--</option>
          <option value="true">Да</option>
          <option value="false">Нет</option>
        </select>
      );
    }
    if (schema.name === 'promo_task' && col.key === 'data') {
      return (
        <textarea
          className="min-h-[120px] w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 disabled:bg-slate-100"
          value={formatPromoDataValue(form[col.key])}
          onChange={e => setForm({ ...form, [col.key]: e.target.value })}
          disabled={!isActive}
        />
      );
    }
    const inputType =
      schema.name === 'users' && col.key === 'password'
        ? 'password'
        : schema.name === 'plan_records' &&
          (col.key === 'date_month_end' || col.key === 'dateMonthEnd')
        ? 'month'
        : col.type === 'date'
        ? 'date'
        : 'text';
    return (
      <input
        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 disabled:bg-slate-100"
        type={inputType}
        value={form[col.key] ?? ''}
        onChange={e => setForm({ ...form, [col.key]: e.target.value })}
        disabled={!isActive}
      />
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm"
      onClick={handleOverlayClick}
    >
      <div className="w-full max-w-md max-h-[80vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
        <h3 className="mb-2 text-lg font-semibold text-slate-800">
          Изменить записи {schema.label || schema.name}
        </h3>
        <p className="mb-4 text-xs text-slate-500">
          Выберите поля для изменения и задайте значения для {ids.length} записей.
        </p>
        {editableColumns.map(col => (
          <div key={col.key} className="mb-3">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              {col.label}
            </label>
            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                className="mt-2 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                checked={!!activeFields[col.key]}
                onChange={() => handleToggleField(col.key)}
              />
              <div className="flex-1">{renderInput(col)}</div>
            </div>
          </div>
        ))}
        <div className="flex justify-end gap-2 pt-2">
          <button
            className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm transition hover:bg-slate-50"
            onClick={onClose}
            disabled={isSaving}
          >
            Отмена
          </button>
          <button
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={handleSave}
            disabled={isSaving}
          >
            Сохранить
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkEditModal;
